import { useState, useEffect, useRef } from 'react';
import { api } from '../api';
import { SettingsState, Task } from '../types';

const BASE = import.meta.env.VITE_API_URL ?? '';

interface Props {
  onCheckingRepliesChange?: (checking: boolean) => void;
  onImportComplete?: () => void;
}

export function Settings({ onCheckingRepliesChange, onImportComplete }: Props) {
  const [settings, setSettings] = useState<SettingsState | null>(null);
  const [checkingReplies, setCheckingReplies] = useState(false);
  const [repliesResult, setRepliesResult] = useState<string | null>(null);

  // Export state
  const [exporting, setExporting] = useState(false);

  // Import state
  const fileInputRef = useRef<HTMLInputElement>(null);
  const [importConfirm, setImportConfirm] = useState<{ tasks: Task[]; filename: string } | null>(null);
  const [importing, setImporting] = useState(false);

  // Toast
  const [toast, setToast] = useState<string | null>(null);

  useEffect(() => {
    api.settings
      .get()
      .then(setSettings)
      .catch(() => setSettings({ lastIngest: null }));
  }, []);

  function showToast(msg: string) {
    setToast(msg);
    setTimeout(() => setToast(null), 4000);
  }

  async function handleCheckReplies() {
    setCheckingReplies(true);
    onCheckingRepliesChange?.(true);
    setRepliesResult(null);
    try {
      const result = await api.checkReplies();
      if (result.message) {
        setRepliesResult(result.message);
      } else if (result.error) {
        setRepliesResult(`Error: ${result.error}`);
      } else {
        setRepliesResult(`Checked ${result.checked} thread${result.checked !== 1 ? 's' : ''} — auto-closed ${result.completed}`);
      }
    } catch (err) {
      setRepliesResult(err instanceof Error ? err.message : 'Request failed');
    } finally {
      setCheckingReplies(false);
      onCheckingRepliesChange?.(false);
    }
  }

  async function handleExport() {
    setExporting(true);
    try {
      const res = await fetch(`${BASE}/api/export`);
      if (!res.ok) throw new Error(`Export failed: ${res.statusText}`);
      const blob = await res.blob();
      const tasks = JSON.parse(await blob.text()) as Task[];
      const date = new Date().toISOString().slice(0, 10);
      const url = URL.createObjectURL(blob);
      const a = document.createElement('a');
      a.href = url;
      a.download = `taskflow-backup-${date}.json`;
      document.body.appendChild(a);
      a.click();
      document.body.removeChild(a);
      URL.revokeObjectURL(url);
      showToast(`Downloaded ${tasks.length} task${tasks.length !== 1 ? 's' : ''}`);
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Export failed');
    } finally {
      setExporting(false);
    }
  }

  function handleFileChange(e: React.ChangeEvent<HTMLInputElement>) {
    const file = e.target.files?.[0];
    if (!file) return;
    // Reset so the same file can be picked again
    e.target.value = '';
    const reader = new FileReader();
    reader.onload = () => {
      try {
        const parsed = JSON.parse(reader.result as string);
        if (!Array.isArray(parsed)) throw new Error('File must contain a JSON array');
        setImportConfirm({ tasks: parsed as Task[], filename: file.name });
      } catch (err) {
        showToast(err instanceof Error ? err.message : 'Could not parse file');
      }
    };
    reader.readAsText(file);
  }

  async function confirmImport() {
    if (!importConfirm) return;
    setImporting(true);
    try {
      const result = await api.importTasks(importConfirm.tasks);
      setImportConfirm(null);
      showToast(`Imported ${result.imported} task${result.imported !== 1 ? 's' : ''} — ${result.total} total in KV`);
      onImportComplete?.();
    } catch (err) {
      showToast(err instanceof Error ? err.message : 'Import failed');
    } finally {
      setImporting(false);
    }
  }

  const webhookUrl = `${BASE}/api/ingest`;

  return (
    <div className="flex-1 overflow-y-auto p-6 max-w-2xl">
      <h1 className="text-lg font-semibold text-white mb-6">Settings</h1>

      {/* Data backup */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Data Backup &amp; Restore
        </h2>
        <p className="text-xs text-gray-500 mb-4">
          Export all tasks to a JSON file for safekeeping. Import a previously exported file to restore or merge tasks — existing tasks with matching IDs are overwritten; new IDs are added.
        </p>
        <div className="flex flex-wrap gap-3">
          <button
            onClick={handleExport}
            disabled={exporting}
            className="text-sm px-4 py-2 bg-gray-800 hover:bg-gray-700 disabled:opacity-50 disabled:cursor-not-allowed border border-gray-700 rounded-lg text-white transition"
          >
            {exporting ? 'Exporting…' : 'Export JSON'}
          </button>
          <button
            onClick={() => fileInputRef.current?.click()}
            className="text-sm px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-white transition"
          >
            Import JSON
          </button>
          <input
            ref={fileInputRef}
            type="file"
            accept=".json,application/json"
            className="hidden"
            onChange={handleFileChange}
          />
        </div>
      </section>

      {/* Ingest webhook */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Ingest Webhook
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          POST tasks from any source to this endpoint. Authenticate with your{' '}
          <code className="text-gray-400 bg-gray-800 px-1 rounded">WEBHOOK_SECRET</code>.
        </p>

        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4 space-y-3">
          <div>
            <p className="text-xs text-gray-500 mb-1">Endpoint</p>
            <code className="text-sm text-indigo-300 break-all">{webhookUrl}</code>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">Example request</p>
            <pre className="text-xs text-gray-300 bg-gray-950 rounded p-3 overflow-x-auto">{`curl -X POST ${webhookUrl} \\
  -H "Authorization: Bearer $WEBHOOK_SECRET" \\
  -H "Content-Type: application/json" \\
  -d '{
    "tasks": [
      {
        "title": "Follow up with Dana",
        "priority": "urgent",
        "source": "manual",
        "dueDate": "2026-07-09"
      }
    ]
  }'`}</pre>
          </div>

          <div>
            <p className="text-xs text-gray-500 mb-1">Response</p>
            <pre className="text-xs text-gray-300 bg-gray-950 rounded p-3">{`{ "added": 1 }`}</pre>
          </div>
        </div>
      </section>

      {/* Gmail auto-close */}
      <section className="mb-8">
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Gmail Auto-Close
        </h2>
        <p className="text-xs text-gray-500 mb-3">
          Check open Gmail tasks and auto-complete any where you've already replied with no
          pending follow-up. Requires <code className="text-gray-400 bg-gray-800 px-1 rounded">GMAIL_AUTH_TOKEN</code> secret on the worker.
        </p>
        <div className="flex items-center gap-3">
          <button
            onClick={handleCheckReplies}
            disabled={checkingReplies}
            className="text-sm px-4 py-2 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white transition"
          >
            {checkingReplies ? 'Checking replies…' : 'Check Replies Now'}
          </button>
          {repliesResult && (
            <span className="text-xs text-gray-400">{repliesResult}</span>
          )}
        </div>
      </section>

      {/* Status */}
      <section>
        <h2 className="text-sm font-semibold text-gray-400 uppercase tracking-wider mb-3">
          Status
        </h2>
        <div className="bg-gray-900 border border-gray-800 rounded-lg p-4">
          <p className="text-xs text-gray-500">Last ingest</p>
          <p className="text-sm text-gray-200 mt-1">
            {settings === null
              ? 'Loading…'
              : settings.lastIngest
              ? new Date(settings.lastIngest).toLocaleString()
              : 'No ingests yet'}
          </p>
        </div>
      </section>

      {/* Import confirmation dialog */}
      {importConfirm && (
        <div className="fixed inset-0 bg-black/60 flex items-center justify-center z-50">
          <div className="bg-gray-900 border border-gray-700 rounded-xl shadow-2xl p-6 w-full max-w-sm mx-4">
            <h3 className="text-base font-semibold text-white mb-2">Confirm Import</h3>
            <p className="text-sm text-gray-400 mb-1">
              File: <span className="text-gray-200">{importConfirm.filename}</span>
            </p>
            <p className="text-sm text-gray-400 mb-4">
              This will write <span className="text-white font-medium">{importConfirm.tasks.length} task{importConfirm.tasks.length !== 1 ? 's' : ''}</span> to KV. Tasks with matching IDs will be overwritten; new IDs will be added.
            </p>
            <div className="flex gap-3 justify-end">
              <button
                onClick={() => setImportConfirm(null)}
                disabled={importing}
                className="text-sm px-4 py-2 bg-gray-800 hover:bg-gray-700 border border-gray-700 rounded-lg text-white transition disabled:opacity-50"
              >
                Cancel
              </button>
              <button
                onClick={confirmImport}
                disabled={importing}
                className="text-sm px-4 py-2 bg-indigo-700 hover:bg-indigo-600 disabled:opacity-50 disabled:cursor-not-allowed rounded-lg text-white transition"
              >
                {importing ? 'Importing…' : 'Import'}
              </button>
            </div>
          </div>
        </div>
      )}

      {/* Toast */}
      {toast && (
        <div className="fixed bottom-6 right-6 bg-gray-800 border border-gray-600 text-white text-sm px-4 py-3 rounded-lg shadow-xl z-50">
          {toast}
        </div>
      )}
    </div>
  );
}
